import { Check, ChevronDown, Plus, Search } from "lucide-react";
import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fieldTypeLabel, type SearchableOption } from "@/lib/forms";
import { cn } from "@/lib/utils";

/** Options grouped under their folder, each with its place in the flat list the keyboard walks. */
function byFolder(options: readonly SearchableOption[]) {
  const groups: { folder: string; items: { option: SearchableOption; index: number }[] }[] = [];
  options.forEach((option, index) => {
    const last = groups.at(-1);
    if (last && last.folder === option.folder) last.items.push({ option, index });
    else groups.push({ folder: option.folder, items: [{ option, index }] });
  });
  return groups;
}

/** Whether every word typed is in the field's title, its folder or its type. */
export function optionMatches(option: SearchableOption, query: string): boolean {
  const text = `${option.title} ${option.folder} ${fieldTypeLabel(option.type)}`.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((word) => text.includes(word));
}

/**
 * One row of Edit searchable fields (ADR-101): a field picked from the form's searchable ones, as
 * GoHighLevel's rows are, with a search over the list because a contact form holds eighty fields. The
 * trigger reads like the design system's Select; the list is a combobox (the search box owns the focus,
 * the arrows walk the options, Enter picks, Escape closes only the list), grouped by folder, each option
 * saying its type. A row that holds a field shows its title and, quieter, where it lives; the blank row at
 * the end of the list says "Add a field". The row is named by what it shows, so a screen reader says what
 * a sighted person reads.
 *
 * The list is a modal popover: inside a modal dialog only the topmost layer may scroll, and a list that
 * is not its own layer would not scroll with a wheel or a trackpad.
 */
export function SearchableFieldPicker({
  value, options, onChange, label, placeholder = "Add a field", invalid, disabled, describedBy, triggerRef,
}: {
  /** The row's field, or null on the blank row. */
  value: { key: string; title: string; detail: string } | null;
  /** What the row may be set to: the searchable fields the other rows do not hold. */
  options: readonly SearchableOption[];
  onChange: (key: string) => void;
  /** What a screen reader calls the list of fields: "Contact fields". */
  label: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  describedBy?: string;
  /** The row's button, for the dialog to put the focus back on a row after it changes the list. */
  triggerRef?: React.Ref<HTMLButtonElement>;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const listId = React.useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const visible = query.trim() ? options.filter((o) => optionMatches(o, query)) : options;
  const groups = byFolder(visible);

  const openWith = (next: boolean) => {
    setOpen(next);
    if (next) {
      setQuery("");
      // Open on the row's own field, so the list shows where it is.
      setActive(Math.max(0, options.findIndex((o) => o.key === value?.key)));
    }
  };
  React.useEffect(() => {
    if (open) document.getElementById(optionId(active))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, open]);

  const pick = (option: SearchableOption | undefined) => {
    if (!option) return;
    setOpen(false);
    if (option.key !== value?.key) onChange(option.key);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const last = visible.length - 1;
    const moves: Record<string, () => number> = {
      ArrowDown: () => Math.min(last, active + 1),
      ArrowUp: () => Math.max(0, active - 1),
      Home: () => 0,
      End: () => Math.max(0, last),
    };
    if (e.key in moves) {
      e.preventDefault();
      setActive(moves[e.key]!());
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(visible[active]);
    }
  };

  return (
    <Popover open={open} onOpenChange={openWith} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-searchable-row=""
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={cn(
            "flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-md border bg-surface px-2.5",
            "text-left text-base text-text shadow-xs transition-[border-color,box-shadow]",
            "focus:border-accent-8 focus:outline-none focus:ring-2 focus:ring-accent-8/25",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted",
            invalid ? "border-danger-9" : open ? "border-accent-8 ring-2 ring-accent-8/25" : "border-border",
            !value && "border-dashed",
          )}
        >
          {value ? (
            <span className="flex min-w-0 flex-1 items-baseline gap-2">
              <span className="truncate">{value.title}</span>
              <span className="truncate text-xs text-text-subtle">{value.detail}</span>
            </span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-text-subtle">
              <Plus className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{placeholder}</span>
            </span>
          )}
          <ChevronDown className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0">
        <div className="border-b border-border p-2">
          <label className="flex h-8 items-center gap-2 rounded-md border border-border bg-surface-sunken px-2">
            <Search className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={visible.length > 0 ? listId : undefined}
              aria-autocomplete="list"
              aria-activedescendant={visible[active] ? optionId(active) : undefined}
              aria-label="Search the fields"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search fields"
              className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-subtle"
            />
          </label>
        </div>
        {visible.length === 0 ? (
          // Outside the list, and a status, so a screen reader says it as the search empties the list.
          <p role="status" className="px-2 py-6 text-center text-sm text-text-muted">
            {options.length === 0 ? "Every field that can be searched is already in the list." : `No field matches "${query.trim()}".`}
          </p>
        ) : (
          <div id={listId} role="listbox" aria-label={label} className="max-h-72 overflow-y-auto p-1 scrollbar-subtle">
            {groups.map((group) => (
              <div key={`${group.folder}-${group.items[0]!.index}`} role="group" aria-label={group.folder}>
                <div className="px-2 pb-1 pt-2 text-2xs font-medium uppercase tracking-wide text-text-subtle" aria-hidden>
                  {group.folder}
                </div>
                {group.items.map(({ option, index }) => {
                  const chosen = option.key === value?.key;
                  return (
                    <div
                      key={option.key}
                      id={optionId(index)}
                      role="option"
                      aria-selected={chosen}
                      onMouseMove={() => setActive(index)}
                      // The search box keeps the focus, so the arrows still work after a hover.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(option)}
                      className={cn(
                        "flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-text",
                        index === active && "bg-surface-hover",
                      )}
                    >
                      <span className="flex w-3.5 shrink-0 justify-center">
                        {chosen && <Check className="size-3.5 text-accent-11" aria-hidden />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{option.title}</span>
                      <span className="shrink-0 text-xs text-text-subtle">{fieldTypeLabel(option.type)}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
